/**
 * Header modules: logo and links.
 *
 * An untouched site uses anchored boxes with the fixed sizes the header has
 * always had. Dragging a module stores a role against the page column
 * (start, center, end) when it snaps, or a column-relative offset when it
 * does not. Older free boxes may still carry viewport vw/dvh until dragged again.
 */

export function defaultHeaderModules() {
    return {
        logo: {
            desktop: { anchor: 'start', x: '1.6em', y: 'center', w: '160px', h: '3.36em', lockRatio: true },
            phone: { anchor: 'start', x: '1.25rem', y: 'center', w: '140px', h: '32px', lockRatio: true },
        },
        links: {
            desktop: { anchor: 'end', x: '1.6em', y: 'center', w: 'auto', h: 'auto', lockRatio: false },
            phone: { anchor: 'end', x: '1.6em', y: 'center', w: 'auto', h: 'auto', hidden: true, lockRatio: false },
        },
    };
}

/** Appearance device layer -> module box key. */
export function breakpointKey(layer) {
    if (layer === 'phone' || layer === 'tablet') return layer;
    return 'desktop';
}

/**
 * The box drawn for a module at a breakpoint.
 * Tablet falls back to desktop. A missing modules object is the classic header.
 */
export function moduleBox(modules, id, layer = 'desktop') {
    const source = modules || defaultHeaderModules();
    const entry = source[id] || defaultHeaderModules()[id] || {};
    const key = breakpointKey(layer);
    if (entry[key]) return entry[key];
    if (entry.desktop) return entry.desktop;
    return defaultHeaderModules()[id]?.desktop || { h: 'auto' };
}

export function lengthNumber(value) {
    if (typeof value === 'number') return value;
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}

export function lengthUnit(value, fallback = 'px') {
    if (typeof value === 'string') {
        const unit = value.trim().match(/[a-z%]+$/i);
        if (unit) return unit[0];
    }
    return fallback;
}

/** Slider path -> which module length it edits. */
const SIZE_SLIDERS = {
    'header.logoWidth': { id: 'logo', key: 'w' },
    'header.logoHeight': { id: 'logo', key: 'h' },
    'header.logoWidthMobile': { id: 'logo', key: 'w', force: 'phone' },
    'header.logoHeightMobile': { id: 'logo', key: 'h', force: 'phone' },
    'header.height': { id: 'bar', key: 'h' },
};

export const LENGTH_UNITS = ['px', 'ch', 'em', 'rem', 'vw', 'dvh', '%'];

/** Preferred unit first, then the rest. */
export function unitsWithPreferred(preferred) {
    const unit = preferred || 'px';
    return [unit, ...LENGTH_UNITS.filter((item) => item !== unit)];
}

/**
 * Page-width slider limits. The token's min/max are for px; other units need
 * their own ceiling so a ch or vw thumb is still usable.
 */
export const PAGE_WIDTH_RANGES = {
    px: { min: 240, max: 3200, step: 10 },
    ch: { min: 40, max: 240, step: 1 },
    em: { min: 20, max: 200, step: 1 },
    rem: { min: 20, max: 200, step: 1 },
    vw: { min: 10, max: 100, step: 0.5 },
    dvh: { min: 10, max: 100, step: 0.5 },
    vh: { min: 10, max: 100, step: 0.5 },
    '%': { min: 10, max: 100, step: 0.5 },
};

export function moduleSliderTarget(path) {
    return SIZE_SLIDERS[path] || null;
}

/**
 * Write a slider number into the module box for the active breakpoint,
 * keeping the unit already stored on that edge.
 */
export function applyModuleSlider(theme, path, value, layer = 'base') {
    const target = SIZE_SLIDERS[path];
    if (!target) return theme;
    const bp = target.force || breakpointKey(layer);
    const modules = theme?.header?.modules || defaultHeaderModules();
    const current = moduleBox(modules, target.id, bp);
    const fallback = target.id === 'bar' || bp === 'phone' || target.key !== 'h' ? 'px' : 'em';
    const rawUnit = lengthUnit(current[target.key], fallback);
    const unit = LENGTH_UNITS.includes(rawUnit) ? rawUnit : fallback;
    const box = { ...current, [target.key]: `${value}${unit}` };
    return writeModuleBox(theme, bp, target.id, box);
}

/** Rewrite one edge in a chosen unit, keeping the number. */
export function applyModuleUnit(theme, path, number, unit, layer = 'base') {
    const target = SIZE_SLIDERS[path];
    if (!target || !unit) return theme;
    const bp = target.force || breakpointKey(layer);
    const modules = theme?.header?.modules || defaultHeaderModules();
    const current = moduleBox(modules, target.id, bp);
    const box = { ...current, [target.key]: `${number}${unit}` };
    return writeModuleBox(theme, bp, target.id, box);
}

/** A number stays in the preferred unit. Any other unit is stored on the value. */
export function storedLength(number, unit, preferred) {
    if (number == null || Number.isNaN(number)) return number;
    if (!unit || unit === preferred) return number;
    return `${number}${unit}`;
}

/** Emit a CSS length. Numbers use the preferred unit; strings that already have one are kept. */
export function cssLength(value, preferred) {
    if (value == null || value === '') return null;
    if (typeof value === 'string' && /[a-z%]+$/i.test(value.trim())) return value.trim();
    return `${value}${preferred}`;
}

/** Keep the same visual size when the unit changes. 1em is 16px; 1ch ≈ half an em. */
export function convertLength(number, from, to, { fontSize = 16, viewportWidth = 1280, viewportHeight = 800 } = {}) {
    if (!Number.isFinite(number) || !to || from === to) return number;
    const chPx = fontSize * 0.5;
    const px = {
        px: number,
        ch: number * chPx,
        em: number * fontSize,
        rem: number * fontSize,
        vw: (viewportWidth * number) / 100,
        dvh: (viewportHeight * number) / 100,
        vh: (viewportHeight * number) / 100,
        '%': (viewportHeight * number) / 100,
    }[from];
    if (!Number.isFinite(px)) return number;
    const out = {
        px,
        ch: px / chPx,
        em: px / fontSize,
        rem: px / fontSize,
        vw: (px / viewportWidth) * 100,
        dvh: (px / viewportHeight) * 100,
        vh: (px / viewportHeight) * 100,
        '%': (px / viewportHeight) * 100,
    }[to];
    return Number.isFinite(out) ? Math.round(out * 100) / 100 : number;
}

/**
 * Width conversion for header modules. `%` is of the page column
 * (min(viewport, page max width)), matching columnSize() in the stylesheet.
 */
export function convertModuleWidth(number, from, to, {
    fontSize = 16,
    viewportWidth = 1280,
    viewportHeight = 800,
    pageMaxWidth = 1600,
} = {}) {
    if (!Number.isFinite(number) || !to || from === to) return number;
    const columnPx = Math.max(1, Math.min(viewportWidth, pageMaxWidth));
    const chPx = fontSize * 0.5;
    const toPx = {
        px: number,
        ch: number * chPx,
        em: number * fontSize,
        rem: number * fontSize,
        vw: (viewportWidth * number) / 100,
        dvh: (viewportHeight * number) / 100,
        vh: (viewportHeight * number) / 100,
        '%': (columnPx * number) / 100,
    }[from];
    if (!Number.isFinite(toPx)) return number;
    const out = {
        px: toPx,
        ch: toPx / chPx,
        em: toPx / fontSize,
        rem: toPx / fontSize,
        vw: (toPx / viewportWidth) * 100,
        dvh: (toPx / viewportHeight) * 100,
        vh: (toPx / viewportHeight) * 100,
        '%': (toPx / columnPx) * 100,
    }[to];
    return Number.isFinite(out) ? Math.round(out * 100) / 100 : number;
}

/**
 * Scale box.h by the same factor as a width change, keeping the wordmark's
 * aspect (and the selection outline) tight when width is the driver.
 * @param {Object|null|undefined} box
 * @param {string} nextW
 * @param {Object} [ctx]
 * @returns {string}
 */
export function scaleBoxHeightForWidth(box, nextW, {
    fontSize = 16,
    viewportWidth = 1280,
    viewportHeight = 800,
    pageMaxWidth = 1600,
} = {}) {
    const prevW = box?.w;
    const prevH = box?.h;
    if (!nextW || nextW === 'auto' || !prevW || prevW === 'auto') {
        return prevH && prevH !== 'auto' ? prevH : 'auto';
    }
    if (!prevH || prevH === 'auto') return 'auto';
    const ctx = { fontSize, viewportWidth, viewportHeight, pageMaxWidth };
    const wFrom = lengthUnit(prevW, 'em');
    const wTo = lengthUnit(nextW, wFrom);
    const hUnit = lengthUnit(prevH, 'em');
    const prevWPx = convertModuleWidth(lengthNumber(prevW), wFrom, 'px', ctx);
    const nextWPx = convertModuleWidth(lengthNumber(nextW), wTo, 'px', ctx);
    if (!(prevWPx > 0) || !(nextWPx > 0)) return prevH;
    const prevHPx = convertLength(lengthNumber(prevH), hUnit, 'px', {
        fontSize,
        viewportWidth,
        viewportHeight,
    });
    if (!(prevHPx > 0)) return prevH;
    const nextH = convertLength(prevHPx * (nextWPx / prevWPx), 'px', hUnit, {
        fontSize,
        viewportWidth,
        viewportHeight,
    });
    if (!Number.isFinite(nextH) || nextH <= 0) return prevH;
    return `${nextH}${hUnit}`;
}

export function writeModuleBox(theme, layer, id, box) {
    const bp = breakpointKey(layer);
    const current = theme?.header?.modules || {};
    const modules = {
        logo: { ...(current.logo || defaultHeaderModules().logo) },
        links: { ...(current.links || defaultHeaderModules().links) },
    };
    Object.keys(current).forEach((key) => {
        if (!modules[key]) modules[key] = { ...current[key] };
    });
    modules[id] = { ...(modules[id] || {}), [bp]: box };

    // Keep typed header.blocks boxes in sync when the id matches a block.
    let blocks = theme?.header?.blocks;
    if (Array.isArray(blocks) && blocks.some((block) => block?.id === id)) {
        blocks = blocks.map((block) => {
            if (block.id !== id) return block;
            return {
                ...block,
                boxes: { ...(block.boxes || {}), [bp]: box },
            };
        });
    }

    const header = {
        ...(theme?.header || {}),
        modules,
        ...(Array.isArray(blocks) ? { blocks } : {}),
    };
    return { ...theme, header };
}

// Anchored modules sit in the centered page column. The column itself is the
// full header, so the inset is added here and one parent works at every breakpoint.
const COLUMN_INSET = 'max(0px, (100% - var(--page-max-width, 1600px)) / 2)';
const COLUMN_WIDTH = 'min(100%, var(--page-max-width, 1600px))';

/** How close a dragged edge must be, in px, before it sticks to a guide. */
export const HEADER_SNAP = 8;

/**
 * The centered page column in viewport coordinates, matching COLUMN_INSET.
 * @param {number} viewportWidth
 * @param {number} [pageMaxWidth]
 */
export function columnRect(viewportWidth, pageMaxWidth = 1600) {
    const width = Math.max(0, Math.min(viewportWidth, pageMaxWidth));
    const left = Math.max(0, (viewportWidth - pageMaxWidth) / 2);
    return { left, width, right: left + width, center: left + width / 2 };
}

function percentOf(part, whole, decimals = 1) {
    if (!whole) return '0%';
    const scale = 10 ** decimals;
    const n = Math.round((part / whole) * 100 * scale) / scale;
    return `${Object.is(n, -0) ? 0 : n}%`;
}

/**
 * Write a measured px length back in the unit the box already used.
 * @param {number} px
 * @param {string} previous - existing length string (e.g. "3.36em")
 * @param {{ basis?: number, viewportWidth?: number, viewportHeight?: number, em?: number, rem?: number }} refs
 */
export function resizeLength(px, previous, refs = {}) {
    if (previous == null || previous === '' || previous === 'auto') return null;
    const unit = lengthUnit(previous);
    const round1 = (n) => Math.round(n * 10) / 10;
    const {
        basis = 0,
        viewportWidth = 0,
        viewportHeight = 0,
        em = 16,
        rem = em,
    } = refs;
    switch (unit) {
        case '%':
            return basis ? `${round1((px / basis) * 100)}%` : previous;
        case 'vw':
            return `${round1((px / (viewportWidth || 1)) * 100)}vw`;
        case 'vh':
            return `${round1((px / (viewportHeight || 1)) * 100)}vh`;
        case 'dvh':
            return `${round1((px / (viewportHeight || 1)) * 100)}dvh`;
        case 'em':
            return `${round1(px / (em || 1))}em`;
        case 'rem':
            return `${round1(px / (rem || 1))}rem`;
        case 'px':
            return `${Math.round(px)}px`;
        default:
            return `${round1(px)}${unit}`;
    }
}

/**
 * Which column guide and header guide a box is touching.
 * @returns {{ x: 'start'|'center'|'end'|null, y: 'center'|null }}
 */
export function snapRoles(rect, headerRect, pageMaxWidth = 1600, snap = HEADER_SNAP) {
    const column = columnRect(headerRect?.width || 0, pageMaxWidth);
    const origin = headerRect?.left || 0;
    const left = rect.left;
    const right = rect.left + rect.width;
    const midX = rect.left + rect.width / 2;
    const midY = rect.top + rect.height / 2;
    const headerMid = (headerRect?.top || 0) + (headerRect?.height || 0) / 2;
    const xCandidates = [
        { role: 'start', d: Math.abs(left - (origin + column.left)) },
        { role: 'end', d: Math.abs(right - (origin + column.right)) },
        { role: 'center', d: Math.abs(midX - (origin + column.center)) },
    ].sort((a, b) => a.d - b.d);
    return {
        x: xCandidates[0].d <= snap ? xCandidates[0].role : null,
        y: headerRect?.height && Math.abs(midY - headerMid) <= snap ? 'center' : null,
    };
}

/**
 * The box written when a drag is released. Horizontal snaps become column
 * anchors. Anything else is an offset from the column's left edge, as a
 * percent of the column, never a viewport vw.
 *
 * A resize only changes w/h and keeps their existing units (and leaves x/y
 * alone). A move may rewrite position into sticky roles / column percents.
 */
export function encodeModuleBox({
    rect,
    headerRect,
    pageMaxWidth = 1600,
    snapped,
    mode,
    lockRatio,
    existing,
    em = 16,
    rem = em,
    viewportWidth,
    viewportHeight,
    precision = 1,
    // Brand / logo: height is also the font size. An em width or left offset
    // would then resolve against that new size and the block would jump.
    scaleFont = false,
}) {
    const column = columnRect(headerRect?.width || 0, pageMaxWidth);
    const vw = viewportWidth || headerRect?.width || 0;
    const vh = viewportHeight || 0;

    if (mode === 'resize') {
        const wRefs = { basis: column.width, viewportWidth: vw, viewportHeight: vh, em, rem };
        const hRefs = { basis: headerRect?.height || 0, viewportWidth: vw, viewportHeight: vh, em, rem };
        // Pixel sizes do not depend on the font size this resize is about to set.
        const w = scaleFont
            ? `${Math.round(rect.width)}px`
            : (resizeLength(rect.width, existing?.w, wRefs)
                || percentOf(rect.width, column.width));
        const h = scaleFont
            ? `${Math.round(rect.height)}px`
            : (resizeLength(rect.height, existing?.h, hRefs)
                || percentOf(rect.height, headerRect?.height || 0));
        // The resize handle is the bottom-right corner. Persist position from the
        // opposite corner (top-left) so growing/shrinking never slides the block
        // when the previous box was end-anchored or vertically centered.
        // An em/rem left offset is relative to the module font size. Keeping it
        // while that font size changes slides the block off the outline.
        const colLeft = (headerRect?.left || 0) + column.left;
        const keepStart = !scaleFont
            && existing?.anchor === 'start'
            && existing?.x != null
            && snapped?.x === 'start';
        const anchor = keepStart || snapped?.x === 'start' ? 'start' : 'free';
        const x = keepStart
            ? existing.x
            : percentOf(rect.left - colLeft, column.width);
        const y = percentOf(rect.top - (headerRect?.top || 0), headerRect?.height || 0);
        return {
            anchor,
            x,
            y,
            w,
            h,
            lockRatio: Boolean(lockRatio),
            ...(existing?.fit ? { fit: existing.fit } : {}),
            ...(existing?.textAlign ? { textAlign: existing.textAlign } : {}),
            ...(existing?.align ? { align: existing.align } : {}),
            ...(existing?.valign ? { valign: existing.valign } : {}),
        };
    }

    const colLeft = (headerRect?.left || 0) + column.left;
    const colRight = (headerRect?.left || 0) + column.right;
    const anchor = snapped?.x || 'free';
    let x = '0%';
    if (anchor === 'start') x = percentOf(rect.left - colLeft, column.width, precision);
    else if (anchor === 'end') x = percentOf(colRight - (rect.left + rect.width), column.width, precision);
    else if (anchor === 'free') x = percentOf(rect.left - colLeft, column.width, precision);
    const y = snapped?.y === 'center'
        ? 'center'
        : percentOf(rect.top - (headerRect?.top || 0), headerRect?.height || 0, precision);
    return { anchor, x, y };
}

function columnSpan(value) {
    return `${lengthNumber(value)} * ${COLUMN_WIDTH} / 100`;
}

function anchored(value) {
    if (lengthUnit(value) === '%') return `calc(${COLUMN_INSET} + ${columnSpan(value)})`;
    return `calc(${COLUMN_INSET} + ${value})`;
}

/** Legacy free boxes store vw. New ones store a percent of the column. */
function freeLeft(value) {
    if (lengthUnit(value) === '%') return anchored(value);
    return value;
}

function columnSize(value) {
    if (lengthUnit(value) === '%') return `calc(${columnSpan(value)})`;
    return value;
}

/** Default brand / logo height — also the default text-wordmark font size. */
export const DEFAULT_LOGO_HEIGHT = '3.36em';
export const DEFAULT_LOGO_WIDTH = '160px';
export const DEFAULT_LOGO_HEIGHT_PHONE = '32px';
export const DEFAULT_LOGO_WIDTH_PHONE = '140px';

/**
 * A free logo with no height was drawn in a box as tall as the viewport, so a
 * visible logo was saved with its top far above the header. A top that high
 * only makes sense for that box.
 */
function needsLogoRepair(box) {
    return Boolean(box) && box.anchor === 'free' && (!box.h || box.h === 'auto')
        && typeof box.y === 'string' && box.y.endsWith('dvh') && parseFloat(box.y) < -25;
}

/**
 * A free logo saved with no height was measured while its box was as tall as
 * the screen, so its stored top sits far above the header. The menu saved in
 * that same pass uses the centre of that box as its top, so it lands to the
 * right of the column. Neither coordinate is a placement: put both back on
 * the classic header. A real frame measurement is required so this only runs
 * from the preview.
 *
 * @param {Object} modules
 * @param {Object} measure - { vh, em } in px, from the preview frame
 * @returns {Object} The same object when nothing needed moving
 */
export function repairAutoLogo(modules, { vh, em }) {
    const logo = modules?.logo;
    if (!logo || !vh || !em) return modules;
    const layers = Object.keys(logo).filter((bp) => needsLogoRepair(logo[bp]));
    if (!layers.length) return modules;
    const logoDefaults = defaultHeaderModules().logo;
    const linkDefaults = defaultHeaderModules().links;
    const nextLogo = { ...logo };
    const nextLinks = { ...(modules.links || {}) };
    layers.forEach((bp) => {
        nextLogo[bp] = { ...(logoDefaults[bp] || logoDefaults.desktop) };
        if (nextLinks[bp]?.anchor === 'free') {
            nextLinks[bp] = { ...(linkDefaults[bp] || linkDefaults.desktop) };
        }
    });
    return { ...modules, logo: nextLogo, links: nextLinks };
}

/**
 * Same repair as repairAutoLogo, also rewriting typed header.blocks boxes.
 * @param {Object} theme
 * @param {{ vh: number, em: number }} measure
 */
export function repairAutoLogoTheme(theme, measure) {
    const modules = theme?.header?.modules;
    const repaired = repairAutoLogo(modules, measure);
    if (!repaired || repaired === modules) return theme;
    let blocks = theme?.header?.blocks;
    if (Array.isArray(blocks)) {
        blocks = blocks.map((block) => {
            if (block.id === 'logo' && repaired.logo) {
                return { ...block, boxes: { ...repaired.logo } };
            }
            if (block.id === 'links' && repaired.links) {
                return { ...block, boxes: { ...repaired.links } };
            }
            return block;
        });
    }
    return {
        ...theme,
        header: {
            ...(theme.header || {}),
            modules: repaired,
            ...(Array.isArray(blocks) ? { blocks } : {}),
        },
    };
}

function declarations(box, { scaleFont = false } = {}) {
    if (!box || box.hidden) return ['display:none'];
    const decls = ['position:absolute'];
    const yCenter = box.y === 'center';
    const xCenter = box.anchor === 'center';

    // Each breakpoint's rule also clears the side it does not use. Otherwise a
    // free `left` from desktop wins over a phone `right` and the box lands off screen.
    if (box.anchor === 'free') {
        decls.push(`left:${freeLeft(box.x)}`, 'right:auto');
        if (yCenter) decls.push('top:50%');
        else decls.push(`top:${box.y}`);
    } else if (box.anchor === 'end') {
        decls.push(`right:${anchored(box.x)}`, 'left:auto');
        decls.push(yCenter ? 'top:50%' : `top:${box.y}`);
    } else if (xCenter) {
        decls.push('left:50%', 'right:auto');
        decls.push(yCenter ? 'top:50%' : `top:${box.y}`);
    } else {
        decls.push(`left:${anchored(box.x)}`, 'right:auto');
        decls.push(yCenter ? 'top:50%' : `top:${box.y}`);
    }

    if (yCenter && xCenter) decls.push('transform:translate(-50%,-50%)');
    else if (yCenter) decls.push('transform:translateY(-50%)');
    else if (xCenter && box.anchor !== 'free') decls.push('transform:translateX(-50%)');
    else decls.push('transform:none');

    if (box.fit === 'hug') {
        // The box grows with its text instead of holding a dragged size.
        // Brand wordmarks keep `h` as the font size; menus use the nav token.
        decls.push('width:max-content', 'height:auto', 'max-width:100%', 'white-space:nowrap');
        if (scaleFont) {
            const fontSize = (box.h && box.h !== 'auto') ? box.h : DEFAULT_LOGO_HEIGHT;
            decls.push(`font-size:${fontSize}`);
        } else {
            decls.push('font-size:var(--header-nav-size, 1em)');
        }
    } else {
        if (box.w && box.w !== 'auto') decls.push(`width:${columnSize(box.w)}`);
        else if (box.anchor === 'free') {
            // A moved menu keeps its own width. Shrink-to-fit against `left`
            // would wrap the labels and make the block taller.
            decls.push('width:max-content', 'max-width:100%', 'white-space:nowrap');
        }
        if (box.h && box.h !== 'auto') {
            if (scaleFont && box.w && box.w !== 'auto' && lengthUnit(box.h) !== '%') {
                // Fixed-width brand wordmark: h is type size and scales with w.
                // Height follows h so the outline stays tight to the glyphs.
                // (Percent h is of the header — handled below with container-type.)
                decls.push(
                    `font-size:${box.h}`,
                    `height:${box.h}`,
                    'max-width:100%',
                    'white-space:nowrap',
                    'line-height:1',
                );
            } else {
                decls.push(`height:${box.h}`);
                if (scaleFont) {
                    const unit = lengthUnit(box.h);
                    // em/rem/% heights must stay relative to the box, not become a
                    // nested font-size (3.36em tall → 3.36× that, or 40% of the
                    // parent font instead of 40% of the header).
                    if (unit === 'em' || unit === 'rem' || unit === '%') decls.push('container-type:size');
                    else decls.push(`font-size:${box.h}`);
                } else {
                    decls.push('font-size:var(--header-nav-size, 1em)');
                }
            }
        } else if (scaleFont) {
            // The wordmark sizes itself to 100cqh of its box. With no height and no
            // size container that falls back to the viewport, so the logo box would
            // be as tall as the screen. Use the default logo height instead.
            decls.push('font-size:var(--header-nav-size, 1em)', `height:${DEFAULT_LOGO_HEIGHT}`, 'container-type:size');
        } else {
            decls.push('font-size:var(--header-nav-size, 1em)');
        }
    }

    if (box.textAlign === 'left' || box.textAlign === 'center' || box.textAlign === 'right') {
        decls.push(`text-align:${box.textAlign}`);
    }
    // A hidden box at another breakpoint only sets display:none. Without an
    // explicit display here, that none stays in effect where this box is shown.
    if (box.align || box.valign) {
        const flex = { start: 'flex-start', center: 'center', end: 'flex-end' };
        const wraps = box.fit !== 'hug' && box.w && box.w !== 'auto';
        decls.push('display:flex', `flex-wrap:${wraps ? 'wrap' : 'nowrap'}`);
        decls.push(`justify-content:${flex[box.align] || 'flex-start'}`);
        decls.push(`align-items:${flex[box.valign] || 'center'}`);
        decls.push(`align-content:${flex[box.valign] || 'center'}`);
    } else {
        decls.push('display:block');
    }
    return decls;
}

/**
 * @param {string} selector
 * @param {Object} box
 * @param {{ scaleFont?: boolean }} [opts]
 */
export function ruleForBox(selector, box, { scaleFont = false } = {}) {
    return `${selector}{${declarations(box, { scaleFont }).join(';')};}`;
}

function rule(selector, box) {
    // Only the logo / brand wordmark scales with its box. The menu keeps the
    // nav size, so a dragged height never becomes a larger font.
    const scaleFont = selector.includes('header-module-logo')
        || selector.includes('header-module-brand');
    return ruleForBox(selector, box, { scaleFont });
}

/**
 * @param {Object} modules - modules map and/or synthetic map from blocks
 * @param {string} layer
 * @param {string[]} [contentIds] - module ids that contribute to auto height
 */
function heightFor(modules, layer, contentIds = ['logo', 'links']) {
    const explicit = moduleBox(modules, 'bar', layer);
    if (explicit?.h && explicit.h !== 'auto') return explicit.h;
    const bottoms = contentIds.map((id) => {
        const box = moduleBox(modules, id, layer);
        if (!box || box.hidden || box.anchor !== 'free') return null;
        if (!box.y || !box.h || box.y === 'center' || box.h === 'auto') return null;
        // A percent is of the header. Using it to size the header would loop.
        if (lengthUnit(box.y) === '%' || lengthUnit(box.h) === '%') return null;
        return `calc(${box.y} + ${box.h})`;
    }).filter(Boolean);
    if (bottoms.length === 1) return bottoms[0];
    if (bottoms.length > 1) return `max(${bottoms.join(',')})`;
    const brandId = contentIds.find((id) => id === 'logo') || contentIds[0];
    const h = (brandId ? moduleBox(modules, brandId, layer).h : null) || '3.36em';
    return `calc(var(--header-pad-y, 22px) * 2 + ${h})`;
}

/**
 * Build a modules-shaped map from typed header blocks (id → boxes).
 * @param {Object[]} blocks
 * @param {Object} [fallbackModules]
 */
export function modulesMapFromBlocks(blocks, fallbackModules) {
    const map = { ...(fallbackModules || defaultHeaderModules()) };
    (Array.isArray(blocks) ? blocks : []).forEach((block) => {
        if (block?.id && block.boxes) map[block.id] = block.boxes;
    });
    return map;
}

/**
 * CSS for header modules / blocks. Unscoped rules are desktop. Phone and tablet
 * follow the same cutoffs as the rest of the header. Attribute rules let the
 * Appearance preview force a breakpoint even when the iframe is a different width.
 *
 * @param {Object} modules
 * @param {{ blocks?: Object[], scaleFontIds?: string[] }} [opts]
 * @returns {string}
 */
export function headerModuleStyleSheet(modules, opts = {}) {
    const blocks = Array.isArray(opts.blocks) ? opts.blocks : null;
    const source = blocks
        ? modulesMapFromBlocks(blocks, modules || defaultHeaderModules())
        : (modules || defaultHeaderModules());
    const contentIds = blocks
        ? blocks.map((block) => block.id).filter(Boolean)
        : ['logo', 'links'];
    const scaleFontIds = new Set(
        opts.scaleFontIds
        || (blocks
            ? blocks
                .filter((b) => b.type === 'brand' || b.type === 'menuToggle')
                .map((b) => b.id)
            : ['logo']),
    );

    const desktopHeight = heightFor(source, 'desktop', contentIds);
    const phoneHeight = heightFor(source, 'phone', contentIds);
    const hasTablet = contentIds.some((id) => source[id]?.tablet);
    const tabletHeight = heightFor(source, 'tablet', contentIds);

    const selectorFor = (id) => {
        if (id === 'logo') return '.header-module-logo';
        if (id === 'links') return '.header-module-links';
        return `[data-header-block="${id}"]`;
    };

    const emit = (bp) => contentIds.map((id) => {
        const sel = selectorFor(id);
        const box = moduleBox(source, id, bp);
        return ruleForBox(sel, box, { scaleFont: scaleFontIds.has(id) });
    }).join('\n');

    let css = `
        #header.header-modules{position:relative;box-sizing:border-box;min-height:${desktopHeight};height:${desktopHeight};}
        /* Width, height and type size change with a drag. They must not ease. */
        #header [data-header-module]{
            transition-property:opacity,color,background-color,text-decoration-color,text-decoration;
        }
        .header-column{position:absolute;inset:0;}
        .header-module-fill{font-size:100cqh;line-height:1;display:block;height:100%;}
        /* Hug boxes size the font on the module; the fill must inherit or 100cqh
           falls back to the viewport and the wordmark becomes enormous. */
        [data-fit="hug"] .header-module-fill{font-size:inherit;height:auto;line-height:1;}
        /* Fixed-width wordmarks inherit module font-size; JS may scale to width. */
        [data-header-block-type="brand"][data-fit="fixed"] .header-module-fill{
            font-size:inherit;height:auto;line-height:1;max-width:100%;
        }
        [data-header-block-type="brand"][data-wordmark-fit="width"]{
            line-height:1;overflow:hidden;
        }
        /* Stay invisible until the fitted size is set, so it never grows into place. */
        [data-header-block-type="brand"][data-wordmark-fit="width"]:not([data-wordmark-ready]){
            visibility:hidden;
        }
        [data-header-block-type="brand"][data-wordmark-fit="width"] .header-module-fill{
            display:block;width:100%;height:100%;white-space:nowrap;line-height:1;
            font-size:inherit;
        }
        [data-header-block-type="brand"][data-wordmark-fit="width"] a.logo-img{
            display:block;height:100%;width:100%;line-height:1;
        }
        /* Hover tokens come from each block's inline --hb-hover-* vars. */
        #header [data-header-block-type="brand"] a.logo-img{
            transition:opacity .2s ease,color .2s ease,text-decoration-color .2s ease;
        }
        #header [data-header-block-type="brand"] a.logo-img:hover{
            opacity:var(--hb-hover-opacity, .8);
            color:var(--hb-hover-color, inherit);
            text-decoration:var(--hb-hover-decoration, underline);
        }
        #header [data-header-block-type="menu"] a{
            line-height:var(--menu-leading, 1);
            transition:color .2s ease,opacity .2s ease,text-decoration-color .2s ease;
        }
        #header [data-header-block-type="menu"] a:hover{
            color:var(--hb-hover-color, #9ca3af);
            text-decoration:var(--hb-hover-decoration, none);
            opacity:var(--hb-hover-opacity, 1);
            font-weight:var(--hb-hover-weight, inherit);
            font-style:var(--hb-hover-style, inherit);
        }
        /* Header hamburger: size from the block font-size; three bars via currentColor. */
        #header [data-header-block-type="menuToggle"]{
            line-height:0;
            color:var(--hb-toggle-color, var(--color-text));
            transition:opacity .2s ease,color .2s ease;
        }
        #header [data-header-block-type="menuToggle"]:hover{
            opacity:var(--hb-hover-opacity, 1);
            color:var(--hb-hover-color, var(--hb-toggle-color, var(--color-text)));
        }
        #header [data-header-block-type="menuToggle"] .hamburger-icon{
            display:block;
            position:relative;
            width:1em;
            height:1em;
            font-size:1em;
        }
        #header [data-header-block-type="menuToggle"] .hamburger-icon::before,
        #header [data-header-block-type="menuToggle"] .hamburger-icon::after,
        #header [data-header-block-type="menuToggle"]::before{
            content:"";
            position:absolute;
            left:50%;
            width:1em;
            height:0.06em;
            background-color:currentColor;
            transform:translateX(-50%);
            transform-origin:center center;
            transition:transform .2s, background-color .3s, top .2s, opacity .2s;
            pointer-events:none;
        }
        #header [data-header-block-type="menuToggle"] .hamburger-icon::before{top:0.25em;}
        #header [data-header-block-type="menuToggle"]::before{top:0.5em;}
        #header [data-header-block-type="menuToggle"] .hamburger-icon::after{top:0.75em;}
        #header [data-header-block-type="menuToggle"][data-custom-icon="1"]::before,
        #header [data-header-block-type="menuToggle"][data-custom-icon="1"] .hamburger-icon::before,
        #header [data-header-block-type="menuToggle"][data-custom-icon="1"] .hamburger-icon::after{
            content:none;
        }
        #app-container.show-menu #header [data-header-block-type="menuToggle"][data-custom-icon="1"]{
            color:var(--color-side-menu-text);
        }
        #app-container.show-menu #header [data-header-block-type="menuToggle"] .hamburger-icon::before{
            top:0.5em;
            transform:translateX(-50%) rotate(45deg);
            background-color:var(--color-side-menu-text);
        }
        #app-container.show-menu #header [data-header-block-type="menuToggle"] .hamburger-icon::after{
            top:0.5em;
            transform:translateX(-50%) rotate(-45deg);
            background-color:var(--color-side-menu-text);
        }
        #app-container.show-menu #header [data-header-block-type="menuToggle"]::before{
            opacity:0;
            background-color:var(--color-side-menu-text);
        }
        ${emit('desktop')}
        @media (max-width:600px){
            #header.header-modules{min-height:${phoneHeight};height:${phoneHeight};}
            ${emit('phone')}
        }
    `;

    if (hasTablet) {
        css += `
            @media (min-width:601px) and (max-width:1024px){
                #header.header-modules{min-height:${tabletHeight};height:${tabletHeight};}
                ${emit('tablet')}
            }
        `;
    }

    const force = (bp, height) => {
        const rules = contentIds.map((id) => {
            const sel = selectorFor(id);
            const box = moduleBox(source, id, bp);
            return ruleForBox(
                `html[data-header-bp="${bp}"] ${sel}`,
                box,
                { scaleFont: scaleFontIds.has(id) },
            );
        }).join('\n');
        return `
        html[data-header-bp="${bp}"] #header.header-modules{min-height:${height};height:${height};}
        ${rules}
    `;
    };

    css += force('desktop', desktopHeight);
    css += force('phone', phoneHeight);
    css += force('tablet', hasTablet ? tabletHeight : desktopHeight);
    return css;
}

/** True when this box is not pinned to a column edge or the column center. */
export function isFreeBox(box) {
    return box?.anchor === 'free';
}
